import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { BlockStack, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { CAMPAIGN_BACKGROUND_MAIN } from '~/constants/campaign';
import { GRangeSlider, GVariantBadge } from '~/presentation/ui';

export type TrafficRateRef = {
  getValues: () => number;
};

interface SettingTrafficRateProps {
  trafficRate: number;
  isDisabled: boolean;
  onChange?: (value: number) => void;
}

export const SettingTrafficRate = forwardRef<TrafficRateRef, SettingTrafficRateProps>(
  (props, ref) => {
    const { isDisabled, trafficRate, onChange } = props;

    const getValueTraffic = useCallback(() => {
      return Math.round(trafficRate * 100);
    }, [trafficRate]);

    const [rangeValue, setRangeValue] = useState(getValueTraffic());

    useEffect(() => {
      setRangeValue(getValueTraffic());
    }, [getValueTraffic]);

    const handleRangeSliderChange = useCallback(
      (value: number) => {
        setRangeValue(value);
        onChange?.(value / 100);
      },
      [onChange],
    );

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => {
          const originTraffic = rangeValue / 100;
          return originTraffic;
        },
      }),
      [rangeValue],
    );

    return (
      <BlockStack gap="300">
        <BlockStack>
          <GRangeSlider
            id="traffic-rate-slider"
            output
            label=""
            disabled={isDisabled}
            min={0}
            max={100}
            value={rangeValue}
            backgroundLower={CAMPAIGN_BACKGROUND_MAIN.ORIGIN}
            backgroundUpper={CAMPAIGN_BACKGROUND_MAIN.VARIANT}
            onChange={handleRangeSliderChange}
          />
          <InlineGrid columns="1fr auto">
            <InlineStack gap="200" blockAlign="center">
              <GVariantBadge
                hideVariant
                label="Original"
                variant="A"
                tone="active"
                labelProps={{
                  variant: 'bodyMd',
                }}
              />
              {'-'}
              <Text as="p" fontWeight="semibold" variant="headingMd">
                {rangeValue}%
              </Text>
            </InlineStack>
            <InlineStack gap="200" blockAlign="center">
              <GVariantBadge
                hideVariant
                label="Variant"
                variant="B"
                labelProps={{
                  variant: 'bodyMd',
                }}
              />
              {'-'}
              <Text as="p" fontWeight="semibold" variant="headingMd" numeric>
                {100 - rangeValue}%
              </Text>
            </InlineStack>
          </InlineGrid>
        </BlockStack>
      </BlockStack>
    );
  },
);

SettingTrafficRate.displayName = 'SettingTrafficRate';
